/**
 * @tennis-matches/services/ai-chat
 * HTTP-Client für /api/ai/* (Web + React Native).
 */

import { getSupabaseClient } from "../supabase/index.js";
import { getApiBaseUrl } from "../api/get-api-base-url.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * API-Basis-URL (leer = same-origin auf Web via Vite-Proxy / CF Functions).
 */
export function getAiApiBaseUrl() {
  return getApiBaseUrl();
}

/**
 * @param {Record<string, string>} [extra]
 */
export async function getAiRequestHeaders(extra = {}) {
  const headers = { "Content-Type": "application/json", ...extra };

  try {
    const sb = await getSupabaseClient();
    const { data } = await sb.auth.getSession();
    const token = data?.session?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) {}

  return headers;
}

/**
 * @returns {Promise<{ id: string } | null>}
 */
export async function fetchOrCreateAiThreadRemote() {
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/threads/current`, { headers });
  const data = await res.json().catch(() => ({}));
  if (!data?.ok || !data?.thread?.id) return null;
  return { id: String(data.thread.id) };
}

/**
 * @param {string} threadId
 * @returns {Promise<Array<{ role: string, content: string }>>}
 */
export async function fetchAiThreadMessagesRemote(threadId) {
  const id = __asStr(threadId);
  if (!id) return [];
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(
    `${apiBase}/api/ai/threads/${encodeURIComponent(id)}/messages`,
    { headers },
  );
  const data = await res.json().catch(() => ({}));
  if (!data?.ok || !Array.isArray(data.messages)) return [];
  return data.messages.map((m) => ({
    role: m.role === "assistant" ? "assistant" : "user",
    content: String(m.content || ""),
  }));
}

/**
 * @param {{
 *   threadId?: string,
 *   messages: Array<{ role: string, content: string }>,
 *   pageContext?: object,
 * }} payload
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendAiChatMessageRemote(payload = {}) {
  const apiBase = getAiApiBaseUrl();
  const url = `${apiBase}/api/ai/chat`;
  const headers = await getAiRequestHeaders();
  const body = {
    messages: Array.isArray(payload.messages) ? payload.messages : [],
    ...(payload.threadId ? { threadId: payload.threadId } : {}),
    ...(payload.pageContext && typeof payload.pageContext === "object"
      ? { pageContext: payload.pageContext }
      : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @param {{ playerName?: string, personId?: string }} [opts]
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendTrainingSearchRemote(playerName, opts = {}) {
  const name = __asStr(playerName);
  const personId = __asStr(opts.personId);
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/training-search`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      ...(name ? { playerName: name } : {}),
      ...(personId ? { personId } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendClubTrainersRemote() {
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/club-trainers`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @param {string} query
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendCoachDeskLookupRemote(query, opts = {}) {
  const q = __asStr(query);
  const scopes = Array.isArray(opts.scopes)
    ? opts.scopes.map((s) => __asStr(s)).filter(Boolean)
    : undefined;
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/coachdesk-lookup`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      query: q,
      ...(scopes?.length ? { scopes } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @param {{ unpaidOnly?: boolean }} [opts]
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendCoachDeskOpenInvoicesRemote(opts = {}) {
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/coachdesk-lookup`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      listOpen: true,
      unpaidOnly: opts.unpaidOnly !== false,
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @param {string} name
 * @param {object} [args]
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendExecuteAiToolRemote(name, args = {}) {
  const toolName = __asStr(name);
  if (!toolName)
    return { ok: false, status: 400, data: { reason: "missing-name" } };
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/execute-tool`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      name: toolName,
      args: args && typeof args === "object" ? args : {},
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}

/**
 * @returns {Promise<{ ok: boolean, status: number, data: object }>}
 */
export async function sendTrainingWeekRemote() {
  const apiBase = getAiApiBaseUrl();
  const headers = await getAiRequestHeaders();
  const res = await fetch(`${apiBase}/api/ai/training-week`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: data?.ok === true, status: res.status, data };
}
